import { useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import * as joinRequestService from '../../services/joinRequestService'

export type BackfillPromptProps = {
  diveId: string
  forfeitedCount: number
  onComplete?: () => void
}

const BackfillPrompt = ({
  diveId,
  forfeitedCount,
  onComplete
}: BackfillPromptProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isActionComplete = resultMessage !== null

  const handleNotify = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await joinRequestService.confirmBackfill(
        diveId,
        forfeitedCount
      )
      if (result.notifiedCount > 0) {
        setResultMessage(
          `${result.notifiedCount} requester${
            result.notifiedCount > 1 ? 's' : ''
          } notified`
        )
      } else {
        setResultMessage('No pending requesters available')
      }
      onComplete?.()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to notify requesters'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await joinRequestService.declineBackfill(diveId)
      setResultMessage('Backfill skipped')
      onComplete?.()
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to skip backfill'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card variant='outlined'>
      <CardContent>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
          Spot available
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {forfeitedCount} {forfeitedCount === 1 ? 'spot has' : 'spots have'}{' '}
          opened up on this dive. Would you like to notify pending requesters?
        </Typography>

        {resultMessage && (
          <Alert severity='info' sx={{ mb: 2 }}>
            {resultMessage}
          </Alert>
        )}

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction='row' spacing={1}>
          <Button
            variant='contained'
            size='small'
            onClick={handleNotify}
            disabled={isLoading || isActionComplete}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            Notify pending requesters
          </Button>
          <Button
            variant='outlined'
            size='small'
            onClick={handleSkip}
            disabled={isLoading || isActionComplete}
          >
            Skip
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}

export default BackfillPrompt
